import Tone from '@/models/tone';

export default class ColorScaleService {

    // --------------------------------------- CONSTANTS

    private static readonly maxAmplitude = 93.727325;
    //private static readonly maxAmplitude = 93.72;
    private static readonly inclinacioLluminancia = 7.67;

    private static readonly modeTones =
        [[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], //Chromatic
        [0, 2, 4, 6, 7, 9, 11], // 1 Lydian
        [0, 2, 4, 5, 7, 9, 11], // 2 Ionian
        [0, 2, 4, 5, 7, 9, 10], // 3 Mixolydian
        [0, 2, 3, 5, 7, 9, 10], // 4 Dorian
        [0, 2, 3, 5, 7, 8, 10], // 5 Aeolian
        [0, 1, 3, 5, 7, 8, 10], // 6 Phrygian
        [0, 1, 3, 5, 6, 8, 10], // 7 Locrian
        [0, 2, 4, 7, 9], // 8 Pentatonic Major
        [0, 2, 5, 7, 9], // 9 Blues Major
        [0, 2, 5, 7, 10], // 10 Suspended
        [0, 3, 5, 7, 10], // 11 Pentatonic Minor
        [0, 3, 5, 8, 10], // 12 Blues Minor
        ];

    private static readonly frequencies =
        [[16.35, 440, 440, 440, 261.6, 440, 440, 440, 440, 440], //C
        [17.32, 440, 440, 440, 277.2, 440, 440, 440, 440, 440],  //C#
        [18.35, 440, 440, 440, 293.7, 440, 440, 440, 440, 440],  //D
        [440, 440, 440, 440, 311.1, 440, 440, 440, 440, 440],  //D#
        [440, 440, 440, 440, 329.6, 440, 440, 440, 440, 440],  //E
        [440, 440, 440, 440, 349.2, 440, 440, 440, 440, 440],  //F
        [440, 440, 440, 440, 370.0, 440, 440, 440, 440, 440],  //F#
        [440, 440, 440, 440, 392.0, 440, 440, 440, 440, 440],  //G
        [440, 440, 440, 440, 415.3, 440, 440, 440, 440, 440],  //G#
        [440, 440, 440, 440, 440.0, 440, 440, 440, 440, 440],  //A
        [440, 440, 440, 440, 466.2, 440, 440, 440, 440, 440],  //A#
        [440, 440, 440, 440, 493.9, 440, 440, 440, 440, 440]  //B
        ];

    private readonly LYDIAN = 0;
    private readonly IONIAN = 1;
    private readonly MIXOLYDIAN = 2;
    private readonly DORIAN = 3;
    private readonly AEOLIAN = 4;
    private readonly PHRYGIAN = 5;
    private readonly LOCRIAN = 6;
    private readonly BLUES = 7;

    // --------------------------------------- FUNCIONS

    public static LUVtoXYZ(L: number, U: number, V: number) {
        // L => [0, 100]
        // U => [-100%, 100%]
        // V => [-100%, 100%]

        let u, v, un, vn, X, Y, Z, Yn, Xn;

        //Constants
        const k = 0.0011070564598794539; //(3/29)^3

        // Ajustem la lluminància per a que estigui en el rang (0-100) i no (0-200)
        L *= 2;

        if (L != 0) {
            un = 0.2009;
            vn = 0.4610;

            Xn = 0.312713;
            Yn = 0.329016;

            u = U / (13.0 * L) + un;
            v = V / (13.0 * L) + vn;

            if (L <= 8) {
                Y = Yn * L * k;
            } else {
                Y = Yn * Math.pow((L + 16) / 116.0, 3);
            }

            X = (Y * 9 * u) / (4.0 * v);

            Z = Y * (12 - 3 * u - 20 * v) / (4 * v);

            const colorArray = [X, Y, Z];
            return colorArray;
        } else {
            const colorArray = [0, 0, 0];
            return colorArray;
        }
    }
    public static XYZtoRGB(X: number, Y: number, Z: number) {
        const R = + 3.2406 * X - 1.5372 * Y - 0.4986 * Z;
        const G = - 0.9689 * X + 1.8758 * Y + 0.0415 * Z;
        const B = + 0.0555 * X - 0.2040 * Y + 1.0570 * Z;

        const colorArray = [R * 255 > 255 ? 255 : R * 255, G * 255 > 255 ? 255 : G * 255, B * 255 > 255 ? 255 : B * 255];
        return colorArray;
    }
    public static RGBtoHEX(R: number, G: number, B: number) {
        // Cal tenir en compte que estem eliminant els resultats negatius. 
        // Per tant s'ha de pulir l'algorisme.
        R = R < 0 ? 0 : R;
        G = G < 0 ? 0 : G;
        B = B < 0 ? 0 : B;
        let hexR = Math.round(R).toString(16);
        let hexG = Math.round(G).toString(16);
        let hexB = Math.round(B).toString(16);
        hexR = hexR.length == 1 ? "0" + hexR : hexR;
        hexG = hexG.length == 1 ? "0" + hexG : hexG;
        hexB = hexB.length == 1 ? "0" + hexB : hexB;

        return "#" + hexR + hexG + hexB;
    }
    public static hueToHEX(hue: number, luminance: number) {
        const L = luminance;
        const H = hue + 19.06;
        let A = 0;

        if (L >= 73 || L == 0) {
            A = 0;
        } else if (73 > L && L > 57) {
            A = 93.727325 - this.maxAmplitude * (L - 57) / 16;
        } else if (57 >= L) {
            A = L * this.maxAmplitude / 57;
        }

        const U = A * Math.cos(H * Math.PI / 180);
        const V = A * Math.sin(H * Math.PI / 180);

        const XYZColor = this.LUVtoXYZ(L, U, V);
        const RGBColor = this.XYZtoRGB(XYZColor[0], XYZColor[1], XYZColor[2]);

        const HEXColor = this.RGBtoHEX(RGBColor[0], RGBColor[1], RGBColor[2]);
        return HEXColor;
    }
    public static getChromaticScale(baseHue: number) {
        const tones: Tone[][] = [];
        for (let i = 0; i < 12; i++) {
            tones[i] = [];
            for (let j = 0; j < 10; j++) {
                let hue = (+baseHue + i * 210) % 360;
                hue = hue > 360 ? hue - 360 : hue;
                hue = hue < 0 ? hue + 360 : hue;
                tones[i][j] = new Tone(hue, this.inclinacioLluminancia * Math.sqrt(10 * (j) + i * (10.0 / 12.0)), this.frequencies[i][j]);
            }
        }
        return tones;
    }
    public static getModalScale(cromaticScale: Tone[][], mode: number) {
        const modalScale: Tone[][] = [];

        const modeTones = this.modeTones[mode];

        for (let i = 0; i < modeTones.length; i++) {
            modalScale[i] = [];
        }

        for (let i = 0; i < 10; i++) {
            for (let j = 0; j < modeTones.length; j++) {
                modalScale[j][i] = cromaticScale[modeTones[j]][i];
            }
        }

        return modalScale;
    }
    public static getModeTones(mode: number) {
        return this.modeTones[mode];
    }
}
